import { PointerEvent as ReactPointerEvent, RefObject, useCallback, useRef, useState } from 'react'
import { useLatest } from 'react-use'
import { Plane, Vector2, Vector3 } from 'three'
import {
  findClosestWeightedSnapTarget,
  LineTarget,
  PointTarget,
  useDocumentListener,
} from '@modugen/ui-toolkit'
import { useThree } from '@react-three/fiber'

export type DragMode = 'xy' | 'z'

/** A plain world position (plan tables / images store `position` as `{x,y,z}`, not a Vector3). */
export interface ScenePosition {
  x: number
  y: number
  z: number
}

// Snap a vertical (z) drag to a storey boundary within this world-distance.
const Z_SNAP_TOLERANCE = 0.3

// Clamp the vertical (z) drag so an item can't be moved out of view.
const Z_MIN = -10
const Z_MAX = 50

// Snap an in-plane (xy) drag to another item's snap point within this world-distance.
const XY_SNAP_DISTANCE = 0.5

interface DragContext {
  mode: DragMode
  id: string
  plane: Plane
  offset: Vector3
  origin: ScenePosition
  boundaryValues: number[]
  xySnapTargets: PointTarget[]
  xyLineSnapTargets: LineTarget[]
}

export interface ScenePositionDragPreview {
  id: string
  position: ScenePosition
  /** Whether `position` is snapped onto another item's ortho (horizontal/vertical) alignment line. */
  isOrthoSnap: boolean
}

interface Params {
  /** `model.storey_boundaries` – the z values a vertical drag snaps to. */
  storeyBoundaries: Record<string, number[]>
  setIsDrawingActive: (active: boolean) => void
  /** Commit the dropped position; the optimistic update + persist lives in the caller's mutation. */
  onCommit: (id: string, position: ScenePosition) => void
  /** Points an in-plane (xy) drag snaps to (e.g. other items' corners/center). */
  xySnapTargets?: PointTarget[]
  /** Lines an in-plane (xy) drag snaps to (e.g. other items' ortho extension lines). */
  xyLineSnapTargets?: LineTarget[]
  /**
   * Mirror of every `dragPreview` change, fired synchronously with the state
   * update. For owners whose dragged visual renders in another subtree (e.g. a
   * batched label) and follows the drag through a store instead of a prop.
   */
  onPreviewChange?: (preview: ScenePositionDragPreview | null) => void
}

export interface ScenePositionDrag {
  /** Begin dragging the item `id` (currently at `position`) in-plane (xy) or vertically (z). */
  startDrag: (mode: DragMode, id: string, position: ScenePosition, event: ReactPointerEvent) => void
  /** The in-progress moved position (id + position), or null when not dragging. */
  dragPreview: ScenePositionDragPreview | null
  /** Which move mode is dragging (drives the HUD button's active style). */
  activeDragMode: DragMode | null
  /**
   * Ref mirroring whether a drag is in progress. Read from non-React handlers
   * (e.g. the scene's onPointerMissed) to ignore clicks that happen while moving
   * an item, so a drag doesn't deselect it.
   */
  isDraggingRef: RefObject<boolean>
}

/**
 * Drag-to-move behaviour for a scene item identified by `id` and a world
 * `position` (x/y/z). An in-plane (xy) drag keeps z; a vertical (z) drag keeps
 * xy and snaps to storey boundaries. The pointer move/up listeners are attached
 * to the document and are only active while a drag is in progress; `startDrag`
 * records the drag context and flips the active mode. On drop it reports the new
 * position via `onCommit`. Shared by plan tables, plan images and comments (the
 * comment controller layers its callout-shifting on top of `onCommit`).
 */
const useScenePositionDrag = ({
  storeyBoundaries,
  setIsDrawingActive,
  onCommit,
  xySnapTargets = [],
  xyLineSnapTargets = [],
  onPreviewChange,
}: Params): ScenePositionDrag => {
  const camera = useThree(state => state.camera)
  const gl = useThree(state => state.gl)
  const raycaster = useThree(state => state.raycaster)

  const [dragPreview, setDragPreview] = useState<ScenePositionDragPreview | null>(null)
  const [activeDragMode, setActiveDragMode] = useState<DragMode | null>(null)

  const dragRef = useRef<DragContext | null>(null)
  const lastMovedRef = useRef<ScenePosition | null>(null)

  const isDragging = activeDragMode !== null

  const getWorldOnPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect()
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      )
      raycaster.setFromCamera(ndc, camera)
      const target = new Vector3()
      return raycaster.ray.intersectPlane(plane, target) ? target.clone() : null
    },
    [camera, gl, raycaster],
  )

  const computeMovedPosition = useCallback(
    (
      drag: DragContext,
      clientX: number,
      clientY: number,
    ): { position: ScenePosition; isOrthoSnap: boolean } | null => {
      const world = getWorldOnPlane(clientX, clientY, drag.plane)
      if (!world) return null
      const target = world.sub(drag.offset)
      const origin = drag.origin

      if (drag.mode === 'xy') {
        const snapped = findClosestWeightedSnapTarget(
          new Vector3(target.x, target.y, origin.z),
          [drag.xySnapTargets],
          [drag.xyLineSnapTargets],
          { xyOnly: true, maxDistance: XY_SNAP_DISTANCE },
        )
        if (snapped) {
          return {
            position: { x: snapped.point.x, y: snapped.point.y, z: origin.z },
            isOrthoSnap: !!snapped.isOrthoTarget,
          }
        }
        return { position: { x: target.x, y: target.y, z: origin.z }, isOrthoSnap: false }
      }

      const clampedZ = Math.min(Z_MAX, Math.max(Z_MIN, target.z))
      let newZ = clampedZ
      let nearestDelta = Z_SNAP_TOLERANCE
      drag.boundaryValues.forEach(value => {
        const delta = Math.abs(value - clampedZ)
        if (delta <= nearestDelta) {
          nearestDelta = delta
          newZ = value
        }
      })
      return { position: { x: origin.x, y: origin.y, z: newZ }, isOrthoSnap: false }
    },
    [getWorldOnPlane],
  )

  // Only active while dragging; listen on the document so the drag keeps tracking
  // (and ends) even when the pointer leaves the scene canvas.
  useDocumentListener(
    'pointermove',
    (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const moved = computeMovedPosition(drag, event.clientX, event.clientY)
      if (!moved) return
      lastMovedRef.current = moved.position
      const preview = { id: drag.id, position: moved.position, isOrthoSnap: moved.isOrthoSnap }
      setDragPreview(preview)
      onPreviewChange?.(preview)
    },
    [computeMovedPosition, onPreviewChange],
    isDragging,
  )

  useDocumentListener(
    'pointerup',
    () => {
      const drag = dragRef.current
      const moved = lastMovedRef.current
      dragRef.current = null
      lastMovedRef.current = null
      setIsDrawingActive(false)
      setDragPreview(null)
      onPreviewChange?.(null)
      setActiveDragMode(null)

      if (!drag || !moved) return
      const { origin } = drag
      const positionChanged = moved.x !== origin.x || moved.y !== origin.y || moved.z !== origin.z
      if (!positionChanged) return

      onCommit(drag.id, moved)
    },
    [onCommit, setIsDrawingActive, onPreviewChange],
    isDragging,
  )

  const startDrag = useCallback(
    (mode: DragMode, id: string, position: ScenePosition, event: ReactPointerEvent) => {
      event.stopPropagation()
      const origin = new Vector3(position.x, position.y, position.z)

      let plane: Plane
      if (mode === 'xy') {
        plane = new Plane(new Vector3(0, 0, 1), -origin.z)
      } else {
        const cameraDirection = camera.getWorldDirection(new Vector3())
        const normal = new Vector3(cameraDirection.x, cameraDirection.y, 0)
        if (normal.lengthSq() < 1e-6) normal.set(1, 0, 0)
        normal.normalize()
        plane = new Plane().setFromNormalAndCoplanarPoint(normal, origin)
      }

      const world = getWorldOnPlane(event.clientX, event.clientY, plane)
      const offset = world ? world.sub(origin) : new Vector3()
      const boundaryValues = Object.values(storeyBoundaries).flat()

      dragRef.current = {
        mode,
        id,
        plane,
        offset,
        origin: position,
        boundaryValues,
        xySnapTargets,
        xyLineSnapTargets,
      }
      lastMovedRef.current = null
      setIsDrawingActive(true)
      // Flipping the mode enables the document pointer listeners above.
      setActiveDragMode(mode)
    },
    [
      camera,
      getWorldOnPlane,
      storeyBoundaries,
      setIsDrawingActive,
      xySnapTargets,
      xyLineSnapTargets,
    ],
  )

  const isDraggingRef = useLatest(isDragging)

  return { startDrag, dragPreview, activeDragMode, isDraggingRef }
}

export default useScenePositionDrag
